import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Theme } from '@mui/material';
import Text from '../../text';
import LtList from './lt-list';
import { LoadState, UiOption } from '@/lib/helpers';
import TextInput from '../text-input';
import Checkbox from '../checkbox';
import { createUseStyles } from 'react-jss';
import { debounce } from 'lodash';

interface Props {
  title: ReactNode;
  options: UiOption[];
  checked: string[];
  height: string | number;
  rowHeight?: number;
  onToggle: (values: string[]) => void;
  labelChecks?: boolean;
  loadState: LoadState;
  readonly: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.subtleGray,
    '& .MuiFormControl-root': {
      margin: 0,
      '& label.MuiFormControlLabel-root': {
        marginLeft: 6,
      },
      '& span.MuiButtonBase-root': {
        padding: 0,
      },
    },
  },
}));

const LtSide: FC<Props> = ({
  title,
  options,
  height,
  rowHeight,
  onToggle,
  checked,
  labelChecks,
  loadState,
  readonly,
  disabled,
  searchPlaceholder,
}) => {
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState('');
  const [checkAll, setCheckAll] = useState(false);
  const styles = useStyles();

  const filteredOptions = useMemo(() => {
    const safeFilter = filter.trim().toLowerCase();
    return [...options]
      .filter((opt) => {
        if (!safeFilter) {
          return true;
        }
        const keywordVal = (opt.meta?.keyword as string) || String(opt.label);
        return keywordVal.toLowerCase().includes(safeFilter);
      })
      .sort((aOpt, bOpt) => {
        const aLabel = String((aOpt.meta?.keyword as string) || aOpt.label).toLowerCase();
        const bLabel = String((bOpt.meta?.keyword as string) || bOpt.label).toLowerCase();
        return aLabel.localeCompare(bLabel);
      });
  }, [options, filter]);

  const mutableOptions = useMemo(() => {
    return filteredOptions.filter((opt) => !opt.disabled);
  }, [filteredOptions]);

  const handleCheckAll = (checkedAll: boolean) => {
    setCheckAll(checkedAll);
    onToggle(
      mutableOptions
        .map(({ value }) => value)
        .filter((value) => (checkedAll ? !checked.includes(value) : checked.includes(value)))
    );
  };

  const handleChangeFilter = useCallback((val: string) => {
    setFilter(val);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleChangeKeyword = useCallback(
    debounce((keyword: string) => {
      handleChangeFilter(keyword);
    }, 400),
    [handleChangeFilter]
  );

  const hasOptions = !!mutableOptions.length;
  const textInputDisabled = readonly || disabled;
  const isDisabled = textInputDisabled || !hasOptions;

  useEffect(() => {
    if (!options.length || mutableOptions.length === options.length) {
      setCheckAll(false);
    }
  }, [mutableOptions, options]);

  useEffect(() => {
    debounceHandleChangeKeyword(keyword);
  }, [keyword, debounceHandleChangeKeyword]);

  return (
    <Stack alignItems="flex-start" width="100%">
      <Box mb={0.5}>
        <Text size="medium" bold>
          {title}
        </Text>
      </Box>
      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        width="100%"
        p={1}
        className={styles.header}
      >
        <Box>
          <Checkbox
            name="check-all"
            checked={checkAll}
            onChange={handleCheckAll}
            fullWidth={false}
            disabled={isDisabled}
          />
        </Box>
        <Stack direction="row" flexGrow={1} justifyContent="flex-end">
          <Box width="75%">
            <TextInput
              name="keyword"
              onChange={setKeyword}
              value={keyword}
              size="x-small"
              placeholder={searchPlaceholder || 'Filter by name'}
              clearable
              fullWidth
              disabled={textInputDisabled}
            />
          </Box>
        </Stack>
      </Stack>
      <LtList
        options={filteredOptions}
        filter={filter}
        checked={checked}
        onToggle={onToggle}
        height={height}
        rowHeight={rowHeight}
        labelChecks={labelChecks}
        loadState={loadState}
        readonly={readonly}
        disabled={isDisabled}
      />
    </Stack>
  );
};

export default LtSide;
